import { defineComponent as _defineComponent } from 'vue'
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { computed, ComputedRef, ref, getCurrentInstance } from 'vue';
import { applyReactInVue } from 'vuereact-combined';

import { useBilling } from '@/components/Billing/Billing.composables';
import BillingService from '@/components/Billing/billing-service';
import {
	CREDITS_VIDEO_URL,
	DATE_FORMAT_DISPLAY,
	AUTOMATION_VIDEO_URL,
	TEAM_FEATURES_VIDEO_URL,
	CUSTOMER_FEATURES_VIDEO_URL,
} from '@/components/Billing/billing-service.const';
import {
	PaymentMethodId,
	ProductType,
	FeatureTypes,
	OveragesProductType,
	EntitlementObject,
	Subscription,
} from '@/components/Billing/billing-types';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const.js';
import { InvoiceHistoryContextType } from '@/react_app/components/BillingUsage/InvoiceHistory/types';
import BillingUsageLayout from '@/react_app/components/BillingUsage/Layout';
import { OverviewSectionProps, FeaturesSectionProps } from '@/react_app/components/BillingUsage/Overview/types';
import { PaymentMethodsContextType } from '@/react_app/components/BillingUsage/PaymentMethods/payment-methods-types';
import {
	UserDisplay,
	BillingEmailsConfig,
	OrganizationSettings,
	SettingsContext as SettingsContextType,
} from '@/react_app/components/BillingUsage/Settings/settings-types';
import { UsageHistoryTabProps } from '@/react_app/components/BillingUsage/UsageHistory/types';
import { BillingUsageLayoutDataProviderProps } from '@/react_app/components/BillingUsage/types';
import {
	BILLING_USAGE_HISTORY_AUTOMATION_ROUTE_NAME,
	BILLING_USAGE_HISTORY_PLY_CREDITS_ROUTE_NAME,
	BILLING_USAGE_HISTORY_TEAM_FEATURES_ROUTE_NAME,
	BILLING_USAGE_HISTORY_CUSTOMER_FEATURES_ROUTE_NAME,
} from '@/router/BillingUsage/billing-usage-router.const';
import { User } from '@/store/modules/account/account-types';
import { pluralize } from '@/utils/language-service';

export default /*#__PURE__*/_defineComponent({
  __name: 'BillingUsageLayoutDataProvider',
  props: {
    isLoadingPrices: { type: Boolean },
    renewReadableDate: null,
    readableRenewableAmount: null,
    creditsQuantityAvailableTasks: null,
    automationQuantityAvailableTasks: null
  },
  setup(__props: any) {

const props = __props as {
	isLoadingPrices: boolean;
	renewReadableDate: string;
	readableRenewableAmount: string;
	creditsQuantityAvailableTasks: number;
	automationQuantityAvailableTasks: number;
}



const VueBillingUsageLayout = applyReactInVue(BillingUsageLayout);
const {
	account,
	customerData,
	subscriptions,
	accountAdmins,
	paymentMethods,
	creditsSubscription,
	organizationSettings,
	entitlementsLastRefresh,
	entitlementsIsRefreshing,
	isOveragesEnabled,
	defaultPaymentMethodId,
	automationSubscription,
	teamFeaturesSubscription,
	getOveragesEstimatedCost,
	customerFeatureSubscription,
	useFeatureUsageAnalyticsHistory,
	getInvoices,
	getEntitlements,
	useEntitlementById,
	deletePaymentMethod,
	getFeatureUsageHistory,
	getSubscriptionHistory,
	getOrganizationSettings,
	useDefaultPaymentMethod,
	setDefaultPaymentMethod,
	saveBillingEmailsConfig,
	saveOrganizationSettings,
	setOveragesEnabled,
} = useBilling();
const defaultPaymentMethod = useDefaultPaymentMethod();
const toast = getCurrentInstance()?.proxy.$toast;
const useEntitlement = useEntitlementById();
const subscriptionsArray = computed(() => Object.values(subscriptions.value));
const allSubscriptionsStartDate = computed(() =>
	subscriptionsArray.value
		.map((sub) => sub.startDate)
		.sort((a, b) => {
			const date1 = a ? moment(a) : moment();
			const date2 = b ? moment(b) : moment();
			const diffBetweenDates = date2.diff(date1);
			if (diffBetweenDates > 0) {
				return -1;
			} else if (diffBetweenDates < 0) {
				return 1;
			} else {
				return 0;
			}
		})
);
const teamFeaturesPlanId = computed(() => teamFeaturesSubscription.value.plan.refId);
const teamFeaturesReadableName = computed(
	() =>
		`${BillingService.getPlanReadableName({
			productType: ProductType.TeamFeatures,
			planId: teamFeaturesPlanId.value,
		})}${didCustomerDataLoad.value ? `(${standardMembersSentence.value}, ${premiumMembersSentence.value})` : ''}`
);
const customerFeaturePlanId = computed(() => customerFeatureSubscription.value.plan.refId);
const customerFeatureReadableName = computed(() =>
	BillingService.getPlanReadableName({
		planId: customerFeaturePlanId.value,
		quantity: customerFeaturesQuantityAvailableTasks.value,
		productType: ProductType.CustomerFeatures,
	})
);
const didCustomerDataLoad = computed(() => !isEmpty(customerData.value));
const amountOfStandardMembers = computed(() => customerData.value.standard_users_count);
const amountOfPremiumMembers = computed(() => customerData.value.premium_users_count);
const standardMembersSentence = computed(() => pluralize('standard member', amountOfStandardMembers.value, true));
const premiumMembersSentence = computed(() => pluralize('premium member', amountOfPremiumMembers.value, true));

const customerFeaturesQuantityAvailableTasks = computed(
	() => useEntitlement.value(FeatureTypes.CustomerTasks)?.entitlement?.usage_limit || 0
);
const emailsReceivingReceipts = computed(() => [
	...adminUsersDisplay.value.map(({ email }) => email),
	...(account.value.billing_emails || []),
]);
const earliestStartDate = computed(() => allSubscriptionsStartDate.value[0]);
const quotaEndDate = computed(() =>
	earliestStartDate.value ? moment(earliestStartDate.value).add(1, 'M').format(DATE_FORMAT_DISPLAY) : undefined
);
const quotaStartDate = computed(() => (earliestStartDate.value ? moment(earliestStartDate.value).format(DATE_FORMAT_DISPLAY) : undefined));
const usersEntitlementCurrentUsage = (featureType: FeatureTypes) => useEntitlement.value(featureType)?.entitlement?.current_usage || 0;

const teamFeaturesSection: ComputedRef<FeaturesSectionProps> = computed(() => ({
	noun: 'tasks',
	sectionTitle: 'Team features',
	quotaEndDate: quotaEndDate.value,
	videoUrl: TEAM_FEATURES_VIDEO_URL,
	quotaStartDate: quotaStartDate.value,
	productType: ProductType.TeamFeatures,
	planReadableName: teamFeaturesReadableName.value,
	seeUsageTo: { name: BILLING_USAGE_HISTORY_TEAM_FEATURES_ROUTE_NAME },
	totalTasks: useEntitlement.value(FeatureTypes.TeamTasks)?.entitlement?.usage_limit || 0,
	showPendingDowngrade: BillingService.isPendingDowngrade(teamFeaturesSubscription.value),
	currentUsedTasks: usersEntitlementCurrentUsage(FeatureTypes.TeamTasks),
	onUpgrade: () => window.open(`${process.env.VUE_APP_APP_URL}customize-plan#${ProductType.TeamFeatures}`, '_blank'),
	isOveragesEnabled: false,
	overageAmount: 0,
	estimatedOveragesCost: undefined,
}));
const customerFeaturesSection: ComputedRef<FeaturesSectionProps> = computed(() => ({
	noun: 'tasks',
	quotaEndDate: quotaEndDate.value,
	sectionTitle: 'Customer features',
	quotaStartDate: quotaStartDate.value,
	videoUrl: CUSTOMER_FEATURES_VIDEO_URL,
	productType: ProductType.CustomerFeatures,
	planReadableName: customerFeatureReadableName.value,
	totalTasks: customerFeaturesQuantityAvailableTasks.value,
	seeUsageTo: { name: BILLING_USAGE_HISTORY_CUSTOMER_FEATURES_ROUTE_NAME },
	showPendingDowngrade: BillingService.isPendingDowngrade(customerFeatureSubscription.value),
	currentUsedTasks: usersEntitlementCurrentUsage(FeatureTypes.CustomerTasks),
	onUpgrade: () => window.open(`${process.env.VUE_APP_APP_URL}customize-plan#${ProductType.CustomerFeatures}`, '_blank'),
	isOveragesEnabled: isOveragesEnabled.value,
	overageAmount: usersEntitlementCurrentUsage(FeatureTypes.CustomerTasksOverages),
	estimatedOveragesCost: getOveragesEstimatedCost.value(OveragesProductType.CustomerFeatures),
}));

const isTeamFeaturesFreePlan = computed(() => BillingService.isFreePlan(teamFeaturesSubscription.value.plan));
const isCustomerFeaturesFreePlan = computed(() => BillingService.isFreePlan(customerFeatureSubscription.value.plan));
const isTeamFeaturesOrCustomerFeaturesPaid = computed(() => !isTeamFeaturesFreePlan.value || !isCustomerFeaturesFreePlan.value);

const automationPlanId = computed(() => automationSubscription.value.plan.refId);
const automationTotalTasks = computed(
	() => useEntitlement.value(FeatureTypes.AutomationTasks)?.entitlement?.usage_limit || props.automationQuantityAvailableTasks || 0
);
const isEntitlementUnlimited = (entitlement: EntitlementObject) => BillingService.isEntitlementUnlimited(entitlement);
const automationReadableName = computed(() =>
	getReadableName(
		automationTotalTasks.value,
		automationSubscription.value,
		ProductType.Automation,
		automationPlanId.value,
		FeatureTypes.AutomationTasks,
		'Automation',
		'tasks'
	)
);

const getReadableName = (
	quantity: number,
	subscription: Subscription,
	productType: ProductType,
	planId: string,
	featureType: FeatureTypes,
	productName: string,
	noun: string
) => {
	const isFreePlan = BillingService.isFreePlan(subscription.plan);
	const entitlement = useEntitlement.value(featureType);
	if (!entitlement) {
		return '';
	}
	if (isEntitlementUnlimited(entitlement)) {
		if (isFreePlan) {
			return `${productName} free beta - unlimited ${noun}`;
		}
		return `${productName} - unlimited ${noun}`;
	}
	if (isFreePlan) {
		return `${quantity} free ${noun}${isTeamFeaturesOrCustomerFeaturesPaid.value ? ` for having a paid ${productName}` : ''}`;
	}
	return BillingService.getPlanReadableName({ quantity, planId, productType });
};
const automationFeaturesSection: ComputedRef<FeaturesSectionProps> = computed(() => ({
	noun: 'tasks',
	sectionTitle: 'Automation',
	videoUrl: AUTOMATION_VIDEO_URL,
	quotaEndDate: quotaEndDate.value,
	productType: ProductType.Automation,
	quotaStartDate: quotaStartDate.value,
	totalTasks: automationTotalTasks.value,
	planReadableName: automationReadableName.value,
	seeUsageTo: { name: BILLING_USAGE_HISTORY_AUTOMATION_ROUTE_NAME },
	showPendingDowngrade: BillingService.isPendingDowngrade(automationSubscription.value),
	currentUsedTasks: usersEntitlementCurrentUsage(FeatureTypes.AutomationTasks),
	onUpgrade: () => window.open(`${process.env.VUE_APP_APP_URL}customize-plan#${ProductType.Automation}`, '_blank'),
	isOveragesEnabled: isOveragesEnabled.value,
	overageAmount: usersEntitlementCurrentUsage(FeatureTypes.AutomationTasksOverages),
	estimatedOveragesCost: getOveragesEstimatedCost.value(OveragesProductType.Automation),
}));
const creditsPlanId = computed(() => creditsSubscription.value.plan.refId);
const creditsTotalTasks = computed(
	() => useEntitlement.value(FeatureTypes.Credits)?.entitlement?.usage_limit || props.creditsQuantityAvailableTasks || 0
);
const creditsReadableName = computed(() =>
	getReadableName(
		creditsTotalTasks.value,
		creditsSubscription.value,
		ProductType.Credits,
		creditsPlanId.value,
		FeatureTypes.Credits,
		'Ply credits',
		'credits'
	)
);
const creditsFeaturesSection: ComputedRef<FeaturesSectionProps> = computed(() => ({
	noun: 'credits',
	sectionTitle: 'Ply credits',
	videoUrl: CREDITS_VIDEO_URL,
	productType: ProductType.Credits,
	quotaEndDate: quotaEndDate.value,
	totalTasks: creditsTotalTasks.value,
	quotaStartDate: quotaStartDate.value,
	planReadableName: creditsReadableName.value,
	seeUsageTo: { name: BILLING_USAGE_HISTORY_PLY_CREDITS_ROUTE_NAME },
	showPendingDowngrade: BillingService.isPendingDowngrade(creditsSubscription.value),
	currentUsedTasks: usersEntitlementCurrentUsage(FeatureTypes.Credits),
	onUpgrade: () => window.open(`${process.env.VUE_APP_APP_URL}customize-plan#${ProductType.Credits}`, '_blank'),
	isOveragesEnabled: isOveragesEnabled.value,
	overageAmount: usersEntitlementCurrentUsage(FeatureTypes.CreditsOverages),
	estimatedOveragesCost: getOveragesEstimatedCost.value(OveragesProductType.Credits),
}));

const refreshHeaderSection = computed(() => ({
	onSync: () => getEntitlements({ forceGet: true }),
	lastRefreshDate: entitlementsLastRefresh.value,
	isRefreshing: entitlementsIsRefreshing.value,
}));

const openEditOveragesDialog = async () => {
	if (!isOveragesEnabled.value) {
		AppDialogManagerService.openEnableOveragesDialog();
	} else {
		const dialogParams = {
			height: '400',
			title: 'Disable exceeding quota',
			dangerText: 'This will disable overages for all your plans. If you exceed your plan’s quota, your features will stop working.',
			yesButtonText: 'Disable exceeding quota',
			noButtonText: 'Cancel',
			confirmationText: 'I agree',
			areYouSureText: `Are you sure you want to disable exceeding quota?`,
		};
		const res = await AppDialogManagerService.openDangerConfirmationDialog(dialogParams);
		if (res === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
			setOveragesEnabled({ isOveragesEnabled: !isOveragesEnabled.value });
		}
	}
};

const overviewSection: ComputedRef<OverviewSectionProps> = computed(() => ({
	planRenewDate: props.renewReadableDate,
	isLoadingPrices: props.isLoadingPrices,
	isOveragesEnabled: isOveragesEnabled.value,
	planRenewableAmount: props.readableRenewableAmount,
	emailsReceivingReceipts: emailsReceivingReceipts.value,
	mainCardLast4Digits: defaultPaymentMethod.value?.card?.last4,
	openEditOveragesDialog,
}));

const overviewSectionWithRefresh = computed(() => ({
	overviewSection: overviewSection.value,
	refreshHeaderSection: refreshHeaderSection.value,
}));

const overviewContextValue = computed(() => ({
	overviewSectionWithRefresh: overviewSectionWithRefresh.value,
	teamFeaturesSection: teamFeaturesSection.value,
	customerFeaturesSection: customerFeaturesSection.value,
	automationFeaturesSection: automationFeaturesSection.value,
	creditsFeaturesSection: creditsFeaturesSection.value,
}));

const adminUsersDisplay = computed(() =>
	accountAdmins.value.map((admin: User) => {
		const displayName = admin.name ? admin.name : admin.first_name ? `${admin.first_name} ${admin.last_name}` : admin.email;
		const avatar = admin.google_avatar || admin.avatar || admin?.profile?.avatar || admin?.profile?.google_avatar || '';
		return { avatar, id: admin.id, role: 'Admin', name: displayName, email: admin.email } as UserDisplay;
	})
) as ComputedRef<UserDisplay[]>;

const settingsContextValue = computed<SettingsContextType>(() => ({
	getOrganizationSettings,
	onSaveBillingEmailsConfig,
	onSaveOrganizationSettings,
	users: adminUsersDisplay.value,
	isOveragesEnabled: isOveragesEnabled.value,
	organizationSettings: organizationSettings.value,
	emailsReceivingReceipts: emailsReceivingReceipts.value,
	billingEmailsConfig: BillingEmailsConfig.Notifications,
	isLoadingBillingEmailsConfig: isLoadingBillingEmailsConfig.value,
	isLoadingOrganizationSettings: isLoadingOrganizationSettings.value,
	onAddNewBillingContact: () => console.log('add new contact'),
}));
const isLoadingOrganizationSettings = ref(false);
const onSaveOrganizationSettings = async (organizationSettings: OrganizationSettings) => {
	try {
		isLoadingOrganizationSettings.value = true;
		await saveOrganizationSettings({ organizationSettings });
	} catch (e) {
		console.error('Something went wrong while onSaveOrganizationSettings', e);
		toast?.error('Something went wrong, please try again later.');
	} finally {
		isLoadingOrganizationSettings.value = false;
	}
};
const isLoadingBillingEmailsConfig = ref(false);
const onSaveBillingEmailsConfig = async (billingEmailsConfig: BillingEmailsConfig) => {
	try {
		isLoadingBillingEmailsConfig.value = true;
		await saveBillingEmailsConfig({ billingEmailsConfig });
	} catch (e) {
		console.error('Something went wrong while onSaveBillingEmailsConfig', e);
		toast?.error('Something went wrong, please try again later.');
	} finally {
		isLoadingBillingEmailsConfig.value = false;
	}
};

const paymentMethodsContextValue = computed(
	() =>
		({
			onDeletePaymentMethod,
			onSetDefaultPaymentMethod,
			paymentMethods: Object.values(paymentMethods.value),
			defaultPaymentMethodId: defaultPaymentMethodId.value,
			paymentIdInLoadingToBeDefault: paymentIdInLoadingToBeDefault.value,
		} as PaymentMethodsContextType)
);
const paymentIdInLoadingToBeDefault = ref<PaymentMethodId | null>(null);
const onDeletePaymentMethod = async (paymentMethodId: PaymentMethodId) => {
	try {
		await deletePaymentMethod(paymentMethodId);
	} catch (e) {
		console.error('Something went wrong while deleting payment method', e);
		toast?.error('Something went wrong while deleting payment method, please try again later.');
	}
};
const onSetDefaultPaymentMethod = async (paymentMethodId: PaymentMethodId) => {
	try {
		paymentIdInLoadingToBeDefault.value = paymentMethodId;
		await setDefaultPaymentMethod(paymentMethodId);
	} catch (e) {
		console.error('Something went wrong while onSetDefaultPaymentMethod  ', e);
		toast?.error('Something went wrong, please try again later.');
	} finally {
		paymentIdInLoadingToBeDefault.value = null;
	}
};

const invoiceHistoryContextValue: ComputedRef<InvoiceHistoryContextType> = computed(() => ({ getInvoices }));

const teamFeaturesUsageHistory: ComputedRef<UsageHistoryTabProps> = computed(() => ({
	productType: ProductType.TeamFeatures,
	cardDetails: teamFeaturesSection.value,
	getFeatureUsageHistory: () => getFeatureUsageHistory({ featureId: FeatureTypes.TeamTasks }),
	getSubscriptionHistory: () => getSubscriptionHistory({ productId: ProductType.TeamFeatures }),
	usageHistory: useFeatureUsageAnalyticsHistory.value(FeatureTypes.TeamTasks),
}));
const customerFeaturesUsageHistory: ComputedRef<UsageHistoryTabProps> = computed(() => ({
	productType: ProductType.CustomerFeatures,
	cardDetails: customerFeaturesSection.value,
	getFeatureUsageHistory: () => getFeatureUsageHistory({ featureId: FeatureTypes.CustomerTasks }),
	getSubscriptionHistory: () => getSubscriptionHistory({ productId: ProductType.CustomerFeatures }),
	usageHistory: useFeatureUsageAnalyticsHistory.value(FeatureTypes.CustomerTasks),
}));
const automationUsageHistory: ComputedRef<UsageHistoryTabProps> = computed(() => ({
	productType: ProductType.Automation,
	cardDetails: automationFeaturesSection.value,
	getFeatureUsageHistory: () => getFeatureUsageHistory({ featureId: FeatureTypes.AutomationTasks }),
	getSubscriptionHistory: () => getSubscriptionHistory({ productId: ProductType.Automation }),
	usageHistory: useFeatureUsageAnalyticsHistory.value(FeatureTypes.AutomationTasks),
}));
const plyCreditsUsageHistory: ComputedRef<UsageHistoryTabProps> = computed(() => ({
	productType: ProductType.Credits,
	cardDetails: creditsFeaturesSection.value,
	getFeatureUsageHistory: () => getFeatureUsageHistory({ featureId: FeatureTypes.Credits }),
	getSubscriptionHistory: () => getSubscriptionHistory({ productId: ProductType.Credits }),
	usageHistory: useFeatureUsageAnalyticsHistory.value(FeatureTypes.Credits),
}));

const usageHistoryContextValue = computed(() => ({
	credits: plyCreditsUsageHistory.value,
	automation: automationUsageHistory.value,
	teamFeatures: teamFeaturesUsageHistory.value,
	customerFeatures: customerFeaturesUsageHistory.value,
}));

const billingUsageProps: ComputedRef<BillingUsageLayoutDataProviderProps> = computed(() => ({
	refreshHeaderSection: refreshHeaderSection.value,
	overviewContextValue: overviewContextValue.value,
	settingsContextValue: settingsContextValue.value,
	usageHistoryContextValue: usageHistoryContextValue.value,
	paymentMethodsContextValue: paymentMethodsContextValue.value,
	invoiceHistoryContextValue: invoiceHistoryContextValue.value,
}));

return { __sfc: true,props, VueBillingUsageLayout, account, customerData, subscriptions, accountAdmins, paymentMethods, creditsSubscription, organizationSettings, entitlementsLastRefresh, entitlementsIsRefreshing, isOveragesEnabled, defaultPaymentMethodId, automationSubscription, teamFeaturesSubscription, getOveragesEstimatedCost, customerFeatureSubscription, useFeatureUsageAnalyticsHistory, getInvoices, getEntitlements, useEntitlementById, deletePaymentMethod, getFeatureUsageHistory, getSubscriptionHistory, getOrganizationSettings, useDefaultPaymentMethod, setDefaultPaymentMethod, saveBillingEmailsConfig, saveOrganizationSettings, setOveragesEnabled, defaultPaymentMethod, toast, useEntitlement, subscriptionsArray, allSubscriptionsStartDate, teamFeaturesPlanId, teamFeaturesReadableName, customerFeaturePlanId, customerFeatureReadableName, didCustomerDataLoad, amountOfStandardMembers, amountOfPremiumMembers, standardMembersSentence, premiumMembersSentence, customerFeaturesQuantityAvailableTasks, emailsReceivingReceipts, earliestStartDate, quotaEndDate, quotaStartDate, usersEntitlementCurrentUsage, teamFeaturesSection, customerFeaturesSection, isTeamFeaturesFreePlan, isCustomerFeaturesFreePlan, isTeamFeaturesOrCustomerFeaturesPaid, automationPlanId, automationTotalTasks, isEntitlementUnlimited, automationReadableName, getReadableName, automationFeaturesSection, creditsPlanId, creditsTotalTasks, creditsReadableName, creditsFeaturesSection, refreshHeaderSection, openEditOveragesDialog, overviewSection, overviewSectionWithRefresh, overviewContextValue, adminUsersDisplay, settingsContextValue, isLoadingOrganizationSettings, onSaveOrganizationSettings, isLoadingBillingEmailsConfig, onSaveBillingEmailsConfig, paymentMethodsContextValue, paymentIdInLoadingToBeDefault, onDeletePaymentMethod, onSetDefaultPaymentMethod, invoiceHistoryContextValue, teamFeaturesUsageHistory, customerFeaturesUsageHistory, automationUsageHistory, plyCreditsUsageHistory, usageHistoryContextValue, billingUsageProps }
}

})